import type { TourStep } from '@/components/ui/Tour';
import type { PageTourKey } from './registry';
import {
  type PaymentsManualCollectionContext,
  type PaymentsSettingsTourContext,
  type PaymentsStarterTourOptions,
  getPaymentsSettingsTourSteps,
  getPaymentsStarterTourSteps,
} from './payments-management-tour-steps';
import { schedulePageTourStart } from './scheduleAutoPageTour';

export type PaymentsTourTab =
  | 'student-billing'
  | 'dashboard'
  | 'settings'
  | 'tax'
  | 'discounts'
  | 'promo-codes'
  | 'accounting';

export type {
  PaymentsManualCollectionContext,
  PaymentsSettingsTourContext,
  PaymentsStarterTourOptions,
};

export const PAYMENTS_STARTER_TOUR_KEY = 'academy-payments-starter' as const;

export const PAYMENTS_TAB_TOUR_KEYS: Partial<Record<PaymentsTourTab, PageTourKey>> = {
  'student-billing': 'academy-payments-billing-report',
  dashboard: 'academy-payments-dashboard',
  settings: 'academy-payments-settings',
  discounts: 'academy-payments-discounts',
  'promo-codes': 'academy-payments-promo-codes',
};

export function resolvePaymentsTabTourKey(tab: PaymentsTourTab): PageTourKey | null {
  return PAYMENTS_TAB_TOUR_KEYS[tab] ?? null;
}

export interface PaymentsTabHelpContent {
  title: string;
  description: string;
  tourLabel?: string;
  tourDescription?: string;
}

export const PAYMENTS_TAB_HELP: Record<PaymentsTourTab, PaymentsTabHelpContent> = {
  'student-billing': {
    title: 'Billing Report',
    description:
      'Reconcile attendance against list prices and export a CSV for manual payment collection on Starter.',
    tourLabel: 'Tour this tab',
    tourDescription: 'How the manual billing report works without Stripe Connect.',
  },
  dashboard: {
    title: 'Payments Report',
    description:
      'Review Stripe charges and payouts from your connected account after families pay online.',
    tourLabel: 'Tour this tab',
    tourDescription: 'Navigate the embedded Stripe payments and payouts views.',
  },
  settings: {
    title: 'Settings',
    description:
      'Set payment instructions on Starter, or complete Stripe Connect onboarding and receipt settings on Professional+.',
    tourLabel: 'Tour this tab',
    tourDescription: 'Configure how families pay and how payouts are set up.',
  },
  tax: {
    title: 'Tax',
    description:
      'Manage Stripe Tax registrations and whether lesson prices are tax-inclusive or added at checkout.',
  },
  discounts: {
    title: 'Discounts',
    description:
      'Configure sibling discounts and membership-linked reductions applied automatically at checkout.',
    tourLabel: 'Tour this tab',
    tourDescription: 'Set up automatic discount rules for families.',
  },
  'promo-codes': {
    title: 'Promo codes',
    description: 'Create promotional codes with optional validity windows and redemption limits.',
    tourLabel: 'Tour this tab',
    tourDescription: 'Create and manage lesson promo codes.',
  },
  accounting: {
    title: 'Accounting Integration',
    description:
      'Connect Xero or QuickBooks Online to sync lesson revenue from Stripe on Enterprise plans.',
  },
};

export function getPaymentsTabHelp(tab: PaymentsTourTab): PaymentsTabHelpContent {
  return PAYMENTS_TAB_HELP[tab];
}

/** Starter merges billing + settings into one tour; only auto-start it from Billing Report. */
export function resolvePaymentsAutoTourKey(
  tab: PaymentsTourTab,
  paymentProcessingAllowed: boolean,
): PageTourKey | null {
  if (!paymentProcessingAllowed) {
    return tab === 'student-billing' ? PAYMENTS_STARTER_TOUR_KEY : null;
  }
  return resolvePaymentsTabTourKey(tab);
}

export function getPaymentsTabForTourKey(tourKey: PageTourKey): PaymentsTourTab | null {
  for (const [tab, key] of Object.entries(PAYMENTS_TAB_TOUR_KEYS)) {
    if (key === tourKey) {
      return tab as PaymentsTourTab;
    }
  }
  return null;
}

export function schedulePaymentsSettingsTourStart(
  startTourWithCompletionKey: (completionKey: string, steps: TourStep[]) => void,
  ctx: PaymentsSettingsTourContext,
): () => void {
  return schedulePageTourStart(
    () =>
      startTourWithCompletionKey('academy-payments-settings', getPaymentsSettingsTourSteps(ctx)),
    'academy-payments-settings',
  );
}

export function schedulePaymentsStarterTourStart(
  startTourWithCompletionKey: (completionKey: string, steps: TourStep[]) => void,
  options: PaymentsStarterTourOptions = {},
): () => void {
  return schedulePageTourStart(
    () =>
      startTourWithCompletionKey(
        PAYMENTS_STARTER_TOUR_KEY,
        getPaymentsStarterTourSteps(options),
      ),
    PAYMENTS_STARTER_TOUR_KEY,
  );
}
